var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useOptionalSiteSettings, } from './SiteSettingsContext';
export var ACCENT_OPTIONS = [
    { id: 'cyan', label: 'Cyan', colors: { 400: '#22D3EE', 500: '#06B6D4', 600: '#0891B2' } },
    { id: 'indigo', label: 'Indigo', colors: { 400: '#818CF8', 500: '#6366F1', 600: '#4F46E5' } },
    { id: 'emerald', label: 'Emerald', colors: { 400: '#34D399', 500: '#10B981', 600: '#059669' } },
    { id: 'rose', label: 'Rose', colors: { 400: '#FB7185', 500: '#F43F5E', 600: '#E11D48' } },
    { id: 'amber', label: 'Amber', colors: { 400: '#D8A435', 500: '#B8860B', 600: '#9A7209' } },
];
export var FONT_FAMILY_OPTIONS = [
    { id: 'default', label: 'Orbitron', stack: "'Orbitron', ui-sans-serif, system-ui, -apple-system, sans-serif" },
    { id: 'dm-sans', label: 'DM Sans', stack: "'DM Sans', ui-sans-serif, sans-serif" },
    { id: 'jakarta', label: 'Jakarta', stack: "'Plus Jakarta Sans', ui-sans-serif, sans-serif" },
    { id: 'atkinson', label: 'Atkinson Hyperlegible', stack: "'Atkinson Hyperlegible', ui-sans-serif, sans-serif" },
    { id: 'source-sans', label: 'Source Sans 3', stack: "'Source Sans 3', ui-sans-serif, sans-serif" },
    { id: 'lexend', label: 'Lexend', stack: "'Lexend', ui-sans-serif, sans-serif" },
];
export var FONT_SIZE_OPTIONS = [
    { id: 'compact', label: 'Compact', scale: 0.875, previewPx: 14 },
    { id: 'default', label: 'Default', scale: 1, previewPx: 16 },
    { id: 'comfortable', label: 'Comfortable', scale: 1.125, previewPx: 18 },
    { id: 'large', label: 'Large', scale: 1.2, previewPx: 19 },
];
export var DEFAULT_HUB_COLOR = '#E10600';
export var DEFAULT_APPEARANCE = {
    accent: 'amber',
    fontFamily: 'dm-sans',
    brandFontFamily: 'default',
    fontSize: 'default',
    hubColor: DEFAULT_HUB_COLOR,
};
var STORAGE_KEYS = {
    theme: 'rstc-theme',
    accent: 'theme-accent',
    fontFamily: 'theme-font-family',
    brandFontFamily: 'theme-brand-font-family',
    fontSize: 'theme-font-size',
    hubColor: 'theme-hub-color',
    appearanceSource: 'theme-appearance-source',
};
var ThemeContext = createContext(undefined);
export var useTheme = function () {
    var context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
var isThemeToggleDisabled = process.env.REACT_APP_DISABLE_THEME_TOGGLE === 'true';
var isThemeValue = function (value) { return value === 'light' || value === 'dark'; };
var loadEnumFromStorage = function (key, allowed, defaultValue) {
    if (typeof window === 'undefined')
        return defaultValue;
    var stored = window.localStorage.getItem(key);
    return stored && allowed.includes(stored) ? stored : defaultValue;
};
var HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
var loadHexColorFromStorage = function (key, defaultValue) {
    if (typeof window === 'undefined')
        return defaultValue;
    var stored = window.localStorage.getItem(key);
    return stored && HEX_COLOR_PATTERN.test(stored) ? stored : defaultValue;
};
var getAccentColors = function (accent) { var _a, _b; return (_b = (_a = ACCENT_OPTIONS.find(function (option) { return option.id === accent; })) === null || _a === void 0 ? void 0 : _a.colors) !== null && _b !== void 0 ? _b : ACCENT_OPTIONS[0].colors; };
var GOOGLE_FONT_QUERY_BY_ID = {
    default: 'family=Orbitron:wght@400;500;600;700',
    'dm-sans': 'family=DM+Sans:wght@400;500;700',
    jakarta: 'family=Plus+Jakarta+Sans:wght@400;500;600;700',
    atkinson: 'family=Atkinson+Hyperlegible:wght@400;700',
    'source-sans': 'family=Source+Sans+3:wght@400;600;700',
    lexend: 'family=Lexend:wght@400;500;600;700',
};
var PRELOADED_THEME_FONT_IDS = new Set(['default', 'dm-sans']);
var buildGoogleFontHref = function (fontFamily) {
    var query = GOOGLE_FONT_QUERY_BY_ID[fontFamily];
    return "https://fonts.googleapis.com/css2?".concat(query, "&display=swap");
};
var getRuntimeNonce = function () {
    if (typeof document === 'undefined')
        return '';
    var existingFontStylesheet = document.querySelector('link[rel="stylesheet"][href*="fonts.googleapis.com"]');
    return (existingFontStylesheet === null || existingFontStylesheet === void 0 ? void 0 : existingFontStylesheet.getAttribute('nonce')) || '';
};
var ensureThemeFontLoaded = function (fontFamily) {
    if (typeof document === 'undefined' || PRELOADED_THEME_FONT_IDS.has(fontFamily)) {
        return;
    }
    var href = buildGoogleFontHref(fontFamily);
    if (document.querySelector("link[data-theme-font-id=\"".concat(fontFamily, "\"]")) || document.querySelector("link[href=\"".concat(href, "\"]"))) {
        return;
    }
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-theme-font-id', fontFamily);
    var nonce = getRuntimeNonce();
    if (nonce && nonce !== '__NONCE__') {
        link.setAttribute('nonce', nonce);
    }
    document.head.appendChild(link);
};
export var ThemeProvider = function (_a) {
    var _b, _c;
    var children = _a.children;
    var siteSettingsContext = useOptionalSiteSettings();
    var globalAppearance = (_c = (_b = siteSettingsContext === null || siteSettingsContext === void 0 ? void 0 : siteSettingsContext.settings) === null || _b === void 0 ? void 0 : _b.appearance) !== null && _c !== void 0 ? _c : DEFAULT_APPEARANCE;
    var _d = useState('dark'), theme = _d[0], setThemeState = _d[1];
    var _e = useState(function () {
        return loadEnumFromStorage(STORAGE_KEYS.accent, ACCENT_OPTIONS.map(function (opt) { return opt.id; }), DEFAULT_APPEARANCE.accent);
    }), localAccent = _e[0], setLocalAccent = _e[1];
    var _f = useState(function () {
        return loadEnumFromStorage(STORAGE_KEYS.fontFamily, FONT_FAMILY_OPTIONS.map(function (opt) { return opt.id; }), DEFAULT_APPEARANCE.fontFamily);
    }), localFontFamily = _f[0], setLocalFontFamily = _f[1];
    var _g = useState(function () {
        return loadEnumFromStorage(STORAGE_KEYS.brandFontFamily, FONT_FAMILY_OPTIONS.map(function (opt) { return opt.id; }), DEFAULT_APPEARANCE.brandFontFamily);
    }), localBrandFontFamily = _g[0], setLocalBrandFontFamily = _g[1];
    var _h = useState(function () {
        return loadEnumFromStorage(STORAGE_KEYS.fontSize, FONT_SIZE_OPTIONS.map(function (opt) { return opt.id; }), DEFAULT_APPEARANCE.fontSize);
    }), localFontSize = _h[0], setLocalFontSize = _h[1];
    var _j = useState(function () {
        return loadHexColorFromStorage(STORAGE_KEYS.hubColor, DEFAULT_APPEARANCE.hubColor);
    }), localHubColor = _j[0], setLocalHubColor = _j[1];
    var _k = useState(function () {
        return loadEnumFromStorage(STORAGE_KEYS.appearanceSource, ['global', 'local'], 'global');
    }), appearanceSource = _k[0], setAppearanceSourceState = _k[1];
    var effectiveAppearance = useMemo(function () {
        if (appearanceSource === 'local') {
            return {
                accent: localAccent,
                fontFamily: localFontFamily,
                brandFontFamily: localBrandFontFamily,
                fontSize: localFontSize,
                hubColor: localHubColor,
            };
        }
        return globalAppearance;
    }, [appearanceSource, localAccent, localFontFamily, localBrandFontFamily, localFontSize, localHubColor, globalAppearance]);
    useEffect(function () {
        if (isThemeToggleDisabled) {
            localStorage.removeItem(STORAGE_KEYS.theme);
            setThemeState('dark');
            return;
        }
        var savedTheme = localStorage.getItem(STORAGE_KEYS.theme);
        if (isThemeValue(savedTheme)) {
            setThemeState(savedTheme);
            return;
        }
        var prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        var systemTheme = prefersDark ? 'dark' : 'light';
        setThemeState(systemTheme);
        localStorage.setItem(STORAGE_KEYS.theme, systemTheme);
    }, []);
    useEffect(function () {
        var root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        if (!isThemeToggleDisabled) {
            localStorage.setItem(STORAGE_KEYS.theme, theme);
        }
    }, [theme]);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f;
        var root = window.document.documentElement;
        var accentColors = getAccentColors(effectiveAppearance.accent);
        var fontStack = (_b = (_a = FONT_FAMILY_OPTIONS.find(function (option) { return option.id === effectiveAppearance.fontFamily; })) === null || _a === void 0 ? void 0 : _a.stack) !== null && _b !== void 0 ? _b : FONT_FAMILY_OPTIONS[0].stack;
        var brandFontStack = (_d = (_c = FONT_FAMILY_OPTIONS.find(function (option) { return option.id === effectiveAppearance.brandFontFamily; })) === null || _c === void 0 ? void 0 : _c.stack) !== null && _d !== void 0 ? _d : FONT_FAMILY_OPTIONS[0].stack;
        var fontScale = (_f = (_e = FONT_SIZE_OPTIONS.find(function (option) { return option.id === effectiveAppearance.fontSize; })) === null || _e === void 0 ? void 0 : _e.scale) !== null && _f !== void 0 ? _f : FONT_SIZE_OPTIONS[1].scale;
        root.style.setProperty('--accent-400', accentColors[400]);
        root.style.setProperty('--accent-500', accentColors[500]);
        root.style.setProperty('--accent-600', accentColors[600]);
        root.style.setProperty('--theme-font-family', fontStack);
        root.style.setProperty('--theme-brand-font-family', brandFontStack);
        root.style.setProperty('--theme-font-scale', String(fontScale));
        root.style.setProperty('--hub-color', effectiveAppearance.hubColor || DEFAULT_HUB_COLOR);
        // Backward-compatible theme tokens.
        root.style.setProperty('--lamborghini-gold', accentColors[500]);
        root.style.setProperty('--lamborghini-yellow', accentColors[400]);
    }, [effectiveAppearance]);
    useEffect(function () {
        ensureThemeFontLoaded(effectiveAppearance.fontFamily);
        ensureThemeFontLoaded(effectiveAppearance.brandFontFamily);
    }, [effectiveAppearance.fontFamily, effectiveAppearance.brandFontFamily]);
    useEffect(function () {
        var handleStorage = function (event) {
            if (event.key === STORAGE_KEYS.theme && isThemeValue(event.newValue)) {
                setThemeState(event.newValue);
            }
            if (event.key === STORAGE_KEYS.accent) {
                var accent = loadEnumFromStorage(STORAGE_KEYS.accent, ACCENT_OPTIONS.map(function (opt) { return opt.id; }), localAccent);
                setLocalAccent(accent);
            }
            if (event.key === STORAGE_KEYS.fontFamily) {
                var fontFamily = loadEnumFromStorage(STORAGE_KEYS.fontFamily, FONT_FAMILY_OPTIONS.map(function (opt) { return opt.id; }), localFontFamily);
                setLocalFontFamily(fontFamily);
            }
            if (event.key === STORAGE_KEYS.brandFontFamily) {
                var brandFontFamily = loadEnumFromStorage(STORAGE_KEYS.brandFontFamily, FONT_FAMILY_OPTIONS.map(function (opt) { return opt.id; }), localBrandFontFamily);
                setLocalBrandFontFamily(brandFontFamily);
            }
            if (event.key === STORAGE_KEYS.fontSize) {
                var fontSize = loadEnumFromStorage(STORAGE_KEYS.fontSize, FONT_SIZE_OPTIONS.map(function (opt) { return opt.id; }), localFontSize);
                setLocalFontSize(fontSize);
            }
            if (event.key === STORAGE_KEYS.hubColor) {
                var hubColor = loadHexColorFromStorage(STORAGE_KEYS.hubColor, localHubColor);
                setLocalHubColor(hubColor);
            }
            if (event.key === STORAGE_KEYS.appearanceSource) {
                var source = loadEnumFromStorage(STORAGE_KEYS.appearanceSource, ['global', 'local'], appearanceSource);
                setAppearanceSourceState(source);
            }
        };
        window.addEventListener('storage', handleStorage);
        return function () { return window.removeEventListener('storage', handleStorage); };
    }, [localAccent, localFontFamily, localBrandFontFamily, localFontSize, localHubColor, appearanceSource]);
    var toggleTheme = function () {
        setThemeState(function (prevTheme) { return (prevTheme === 'light' ? 'dark' : 'light'); });
    };
    var setTheme = function (newTheme) {
        setThemeState(newTheme);
    };
    var setAccent = useCallback(function (accent) {
        setLocalAccent(accent);
        localStorage.setItem(STORAGE_KEYS.accent, accent);
    }, []);
    var setFontFamily = useCallback(function (fontFamily) {
        setLocalFontFamily(fontFamily);
        localStorage.setItem(STORAGE_KEYS.fontFamily, fontFamily);
    }, []);
    var setBrandFontFamily = useCallback(function (fontFamily) {
        setLocalBrandFontFamily(fontFamily);
        localStorage.setItem(STORAGE_KEYS.brandFontFamily, fontFamily);
    }, []);
    var setFontSize = useCallback(function (fontSize) {
        setLocalFontSize(fontSize);
        localStorage.setItem(STORAGE_KEYS.fontSize, fontSize);
    }, []);
    var setHubColor = useCallback(function (hubColor) {
        setLocalHubColor(hubColor);
        localStorage.setItem(STORAGE_KEYS.hubColor, hubColor);
    }, []);
    var setAppearanceSource = useCallback(function (source) {
        setAppearanceSourceState(source);
        localStorage.setItem(STORAGE_KEYS.appearanceSource, source);
    }, []);
    var resetToDefaults = useCallback(function () {
        setAccent(DEFAULT_APPEARANCE.accent);
        setFontFamily(DEFAULT_APPEARANCE.fontFamily);
        setBrandFontFamily(DEFAULT_APPEARANCE.brandFontFamily);
        setFontSize(DEFAULT_APPEARANCE.fontSize);
        setHubColor(DEFAULT_APPEARANCE.hubColor);
    }, [setAccent, setFontFamily, setBrandFontFamily, setFontSize, setHubColor]);
    var value = {
        theme: theme,
        toggleTheme: toggleTheme,
        setTheme: setTheme,
        accent: effectiveAppearance.accent,
        fontFamily: effectiveAppearance.fontFamily,
        brandFontFamily: effectiveAppearance.brandFontFamily,
        fontSize: effectiveAppearance.fontSize,
        hubColor: effectiveAppearance.hubColor,
        setAccent: setAccent,
        setFontFamily: setFontFamily,
        setBrandFontFamily: setBrandFontFamily,
        setFontSize: setFontSize,
        setHubColor: setHubColor,
        appearanceSource: appearanceSource,
        setAppearanceSource: setAppearanceSource,
        resetToDefaults: resetToDefaults,
        accentColors: getAccentColors(effectiveAppearance.accent),
    };
    return _jsx(ThemeContext.Provider, __assign({ value: value }, { children: children }));
};
